import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import AccountCard from '../AccountCard';
import RolloverCard from '../RolloverCard';
import PendingAccountCard from '../PendingAccountCard';
import { PrevArrow, NextArrow } from '../CarouselArrows';
import { sortAccounts, isAccountRollingOver } from '../../utils/accountSorting';
import { productTypes } from '../../../../utils/productHelpers';

/**
 * Accounts List component
 * Renders all account cards in either carousel or grid mode
 * @param {{
 *   accounts: any[];
 *   customer: any;
 *   pendingAccounts: any[];
 *   products: any[];
 *   carouselEnabled: boolean;
 *   selectedAccountActivity: string | null;
 *   flippedAccountId: string | null;
 *   actionType: 'withdraw' | 'deposit' | null;
 *   toAccount?: any;
 *   onToggleActivity: (accountId: string) => void;
 *   onStartWithdrawal: (accountId: string, productType?: string) => void;
 *   onStartDeposit: (accountId: string) => void;
 *   onStartTransfer: (account: any) => void;
 *   onUnflipCard: () => void;
 *   onInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onMaturityInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onAvailableBalanceIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onFundingInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   onRateChangeInfoIconClick: (event: React.MouseEvent, accountId: string) => void;
 *   openTooltipId: string | null;
 *   openMaturityTooltipId: string | null;
 *   openFundingTooltipId: string | null;
 *   openRateChangeTooltipId: string | null;
 *   setActionType: (type: 'withdraw' | 'deposit' | null) => void;
 * }} props
 */
const AccountsList = ({
    accounts,
    customer,
    pendingAccounts,
    products,
    carouselEnabled,
    selectedAccountActivity,
    flippedAccountId,
    actionType,
    toAccount,
    onToggleActivity,
    onStartWithdrawal,
    onStartDeposit,
    onStartTransfer,
    onUnflipCard,
    onInfoIconClick,
    onMaturityInfoIconClick,
    onAvailableBalanceIconClick,
    onFundingInfoIconClick,
    onRateChangeInfoIconClick,
    openTooltipId,
    openMaturityTooltipId,
    openFundingTooltipId,
    openRateChangeTooltipId,
    setActionType
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    // Disable swiping when card is flipped or any tooltip modal is open
    const hasAnyTooltipOpen = openTooltipId !== null || openMaturityTooltipId !== null || openFundingTooltipId !== null || openRateChangeTooltipId !== null;
    const canSwipe = flippedAccountId === null && !hasAnyTooltipOpen;
    const arrowsVisible = flippedAccountId === null;
    
    const { orderedActiveAccounts, closedAccounts } = sortAccounts(accounts);

    const hasDisaAccountAgainstFisa = (/** @type {any} */ account) => {
        if (account?.product?.productType !== productTypes.FixedRateCashISA) return false;
        return (account?.maturity?.maturityInstructionWindowStatus === 'InstructionClosed' && account?.accountStatus === 'Closed')
    };
    
    /**
     * Create cards for a single account (may include rollover card)
     */
    const createAccountCards = (/** @type {any} */ account) => {
        const cards = [];
        const activityIsShowing = selectedAccountActivity === account.id;
        const isFlipped = flippedAccountId === account.id;
        
        cards.push(
            <AccountCard
                key={account.id}
                account={account}
                customer={customer}
                hasDisaAccountAgainstFisa={hasDisaAccountAgainstFisa(account)}
                toAccount={toAccount}
                isFlipped={isFlipped}
                actionType={actionType}
                activityIsShowing={activityIsShowing}
                onToggleActivity={() => onToggleActivity(account.id)}
                onStartWithdrawal={() => onStartWithdrawal(account.id, account.product.productType)}
                onStartDeposit={() => onStartDeposit(account.id)}
                onStartTransfer={() => onStartTransfer(account)}
                onUnflipCard={onUnflipCard}
                onAfterUnflip={() => {
                    if (flippedAccountId === null) setActionType(null);
                }}
                onInfoIconClick={onInfoIconClick}
                onMaturityInfoIconClick={onMaturityInfoIconClick}
                onAvailableBalanceIconClick={onAvailableBalanceIconClick}
                onFundingInfoIconClick={onFundingInfoIconClick}
                onRateChangeInfoIconClick={onRateChangeInfoIconClick}
                openTooltipId={openTooltipId}
                openMaturityTooltipId={openMaturityTooltipId}
                openFundingTooltipId={openFundingTooltipId}
                openRateChangeTooltipId={openRateChangeTooltipId}
                setActionType={setActionType}
            />
        );
        
        // Add rollover card if account is rolling over
        if (isAccountRollingOver(account)) {
            cards.push(
                <RolloverCard
                    key={`${account.id}-rollover`}
                    account={account}
                    products={products}
                />
            );
        }
        
        return cards;
    };
    
    // Build all cards
    /** @type {React.ReactNode[]} */
    const cards = [];
    
    // Active accounts (sorted)
    orderedActiveAccounts.forEach((/** @type {any} */ account) => {
        cards.push(...createAccountCards(account));
    });
    
    // Pending accounts
    (pendingAccounts || []).forEach((/** @type {any} */ pendingAccount, /** @type {number} */ index) => {
        const card = (
            <PendingAccountCard
                key={`pending-${index}`}
                pendingAccount={pendingAccount}
                products={products}
            />
        );
        if (card) cards.push(card);
    });
    
    // Closed accounts
    closedAccounts.forEach((/** @type {any} */ account) => {
        cards.push(...createAccountCards(account));
    });

    useEffect(() => {
        setCurrentSlide(0);
    }, [cards.length]);
    
    // No accounts state
    if (cards.length === 0) {
        return (
            <div className="new-dashboard__no-accounts">
                <p>No accounts found. Open your first account to get started!</p>
            </div>
        );
    }
    
    // Carousel mode for mobile
    if (carouselEnabled) {
        const nextArrowVisible = arrowsVisible && currentSlide < cards.length - 1;
        const prevArrowVisible = arrowsVisible && currentSlide > 0;

        return (
            <Slider
                className="new-dashboard__carousel account-carousel"
                infinite={false}
                speed={500}
                slidesToShow={1}
                slidesToScroll={1}
                arrows={true}
                dots={false}
                nextArrow={<NextArrow enabled={canSwipe && nextArrowVisible} visible={nextArrowVisible} />}
                prevArrow={<PrevArrow enabled={canSwipe && prevArrowVisible} visible={prevArrowVisible} />}
                swipe={canSwipe}
                swipeToSlide={canSwipe}
                draggable={canSwipe}
                touchMove={canSwipe}
                beforeChange={(currentIndex, nextIndex) => setCurrentSlide(nextIndex)}
            >
                {cards}
            </Slider>
        );
    }
    
    // Grid mode for desktop
    return <>{cards}</>;
};

export default AccountsList;
